import React, {useCallback, useEffect, useState} from "react";
import {Combination, MenuItem, MenuItemCombination} from "../../@types/stores/menu";
import {observer} from "mobx-react";
import "./style.scss";
import {getCombinationArticleOptionsUrl, getHost, getNextCombinationStep, getNextPackageArticleOptionsUrl} from "../../helpers/ApiHelper";
import defaultImg from "../../assets/images/default.webp";
import {OrderLineAddon, OrderLineAddons} from "../../@types/stores/order";
import lodash from "lodash";
import {useTranslation} from "react-i18next";
import AddonsGroup from "../MenuItem/AddonsGroup";
import orderStore from "../../stores/order";
import {useLocation, useNavigate, useParams} from "react-router";
import {moveToTop} from "../../helpers/DomHelper";
import useLayoutStyles from "../../hooks/styles";

export interface CombinationArticleAddonsProps {
    combination: MenuItemCombination;
    step: Combination;
    item: MenuItem;
    menuId: number;
    subMenuId: number;
}

const CombinationArticleAddons = ({item, combination, step, menuId, subMenuId}: CombinationArticleAddonsProps) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const styles = useLayoutStyles();

    const [addons, setAddons] = useState<OrderLineAddons[]>([]);
    const [free, setFree] = useState<number>(item.freeAddons || 0);

    useEffect(() => {
        const _addons = orderStore.getCurrentCombinationArticleOrderLine(step, item)?.addons;
        if (_addons?.length && !addons.length)
            setAddons(_addons);
    }, [location]);

    useEffect(() => {
        if (addons.length) {
            const orderLine = orderStore.getCurrentCombinationArticleOrderLine(step, item);
            if (orderLine) {
                orderLine.addons = addons;
                orderStore.updateCurrentCombinationArticleOrderLine(combination, step, orderLine);
            }

            reCalcFreeAddons();
        }
    }, [addons]);

    const reCalcFreeAddons = () => {
        const count = addons.map(x => x.items).flat().length;
        const _free = item.freeAddons || 0;
        setFree(_free - count > 0 ? _free - count : 0);
    }

    const changeAddons = (id: number, selectedAddons: OrderLineAddon[]) => {
        const _addons = lodash.cloneDeep(addons).filter(x => x.groupId !== id);
        const addonGroup = item.addonGroups?.find(x => x.id === id);
        if (!addonGroup) return;

        _addons.push({
            groupId: id,
            groupFreeAddons: addonGroup.freeAddons || 0,
            items: selectedAddons
        });
        setAddons(_addons);
    }

    const goNext = () => {
        const isChange = params.change === "change";

        moveToTop();

        if (combination.isPackage) {
            const nextArticleUrl = getCombinationArticleOptionsUrl(menuId, subMenuId, combination, step, item, isChange, "addons");
            navigate(nextArticleUrl || (isChange
                ? `/menu/combination/info`
                : getNextPackageArticleOptionsUrl(menuId, subMenuId, combination, step, item)));
            return;
        }

        if (item.ingredientGroups?.length) {
            navigate(`/menu/combination/${menuId}/${subMenuId}/${combination.id}/step/${step.id}/article/${item.id}/ingredients${isChange ? "/change" : ""}`);
            return;
        }

        const nextStep = getNextCombinationStep(combination, step);
        if (!isChange && nextStep)
            navigate(`/menu/combination/${menuId}/${subMenuId}/${combination.id}/step/${nextStep.id}`);
        else
            navigate(`/menu/combination/info`);
    }

    const getSelectedAddons = useCallback((id: number) => {
        return addons.find(x => x.groupId === id)?.items || [];
    }, [addons]);

    if (!item)
        return null;

    return <div className="CombinationArticleAddons">
        <h2>{item.name}</h2>
        <div className="img"
             style={{backgroundImage: `url(${item.previewEdit ? `${getHost()}${item.previewEdit}` : (styles?.article.defaultEditImage ? `${getHost()}${styles.article.defaultEditImage}` : defaultImg)}`}}/>
        <div className="description">{item.description || ""}</div>
        <div className="addons">
            {item.addonGroups?.length
                ? item.addonGroups.map(ag => <AddonsGroup key={`addonsGroup_${ag.id}`} addonsGroup={ag}
                                                          onChange={changeAddons}
                                                          selected={getSelectedAddons(ag.id)}
                                                          free={free}/>)
                : null
            }
        </div>
        <div className={`button dark`}
             onClick={goNext}>{t(addons.map(mg => mg.items).flat().length ? `next` : `skip`)}</div>
    </div>
}

export default observer(CombinationArticleAddons);
